import { createSelector } from '@ngrx/store';
import { AppState } from '@store';
import {
  AssistantConcepts,
  AssistantDocumentDetails,
  AssistantFeedback,
  AssistantFilter,
  AssistantSearch,
  AssistantSuggestData,
} from '@vi-iot/client/apis/assistant/assistant.api-model';
import { StatusLoadedValue } from '../app.state.models';
import {
  AssistantDocumentsState,
  AssistantFeedbackState,
  AssistantFilterItem,
  AssistantFilterSection,
  AssistantOverlayState,
  SelectedDocumentsState,
} from './assistant.model';
import { AssistantState } from './assistant.state';

export namespace AssistantSelectors {
  const assistant = (state: AppState): AssistantState => state.assistant;

  export const selectOverlay = createSelector(
    assistant,
    (state: AssistantState): AssistantOverlayState => state.overlay
  );

  export const selectFilters = createSelector(
    assistant,
    (state: AssistantState): StatusLoadedValue<AssistantFilter[]> => {
      return state.filters;
    }
  );

  export const selectSuggestions = createSelector(
    assistant,
    (state: AssistantState): StatusLoadedValue<AssistantSuggestData[]> => {
      return state.suggestions;
    }
  );

  export const getSearchDocuments = createSelector(
    assistant,
    (state: AssistantState): AssistantDocumentsState => {
      return state.searchDocuments;
    }
  );

  export const getSelectedDocumentState = createSelector(
    assistant,
    (state: AssistantState): SelectedDocumentsState => {
      return state.selectedDocument;
    }
  );

  export const getSearchDocumentFromSelectedDocument = createSelector(
    assistant,
    (state: AssistantState): AssistantSearch => {
      return state.selectedDocument.searchDocument;
    }
  );

  export const getSelectedDocumentDetails = createSelector(
    assistant,
    (state: AssistantState): StatusLoadedValue<AssistantDocumentDetails> => {
      return state.selectedDocument.details;
    }
  );

  export const getSelectedDocumentContent = createSelector(
    assistant,
    (state: AssistantState): StatusLoadedValue<string> => {
      return state.selectedDocument.content;
    }
  );

  export const selectAssistantFeedbackState = createSelector(
    assistant,
    (state: AssistantState): AssistantFeedbackState => {
      return state.assistantFeedbacks;
    }
  );

  export const getSearchQuery = createSelector(assistant, (state: AssistantState): string => {
    return state.searchQuery;
  });

  export const getSelectedFilters = createSelector(
    assistant,
    (state: AssistantState): AssistantFilterItem[] => {
      return state.selectedFilters;
    }
  );

  export const isFiltersOrDocumentsLoading = createSelector(
    assistant,
    (state: AssistantState): boolean => {
      return state.filters.loading || state.searchDocuments.loading;
    }
  );

  export const selectAssistantFeedbackData = createSelector(
    assistant,
    (state: AssistantState): AssistantFeedback[] => state.assistantFeedbacks.feedbackData.value
  );

  export const getFilterDropdownOptions = createSelector(
    assistant,
    (state: AssistantState): AssistantFilterSection[] => {
      return state.filters.value
        .map((filter: AssistantFilter) => {
          switch (filter.dataType) {
            case 'string':
              return {
                items: [
                  {
                    name: filter.label,
                    value: { attribute: filter.attribute },
                    children: mapConceptsToChildren(filter.attribute, filter.concepts),
                  },
                ],
              };
            case 'taxonomy':
              return {
                label: filter.label,
                items: filter.concepts
                  .filter(
                    (concept: AssistantConcepts) =>
                      concept.hasChildren || (!concept.hasChildren && concept.parentId === '')
                  )
                  .map((parent: AssistantConcepts) => {
                    const isStandAloneParent = !parent.hasChildren && parent.parentId === '';
                    if (isStandAloneParent) {
                      return {
                        name: parent.label,
                        isStandAloneParent: isStandAloneParent,
                        value: {
                          attribute: filter.attribute,
                          concept: {
                            count: parent.count,
                            id: parent.label,
                            hasChildren: parent.hasChildren,
                            label: parent.label,
                            parentId: parent.parentId,
                          },
                        },
                      };
                    } else {
                      return {
                        name: parent.label,
                        isStandAloneParent: isStandAloneParent,
                        children: mapConceptsToChildren(
                          filter.attribute,
                          filter.concepts.filter(
                            (concept: AssistantConcepts) => concept.parentId === parent.id
                          )
                        ),
                      };
                    }
                  }),
              };
            default:
              return null;
          }
        })
        .filter((section: AssistantFilterSection) => section !== null);
    }
  );

  function mapConceptsToChildren(
    attribute: string,
    concepts: AssistantConcepts[]
  ): AssistantFilterItem[] {
    return concepts.map((concept: AssistantConcepts) => ({
      name: concept.label,
      value: {
        attribute: attribute,
        concept: concept,
      },
    }));
  }

  export const selectIsSelectedDocumentContentError = createSelector(
    assistant,
    (state: AssistantState): boolean => !!state.selectedDocument.content.error
  );
}
